/**
 * Generic Files tab — drop into any record page.
 *
 * Usage:
 *   <FilesTab parent="hr.person" parentId={person.id} />
 *   <FilesTab parent="ats.application" parentId={app.id} />
 *
 * The component handles upload (click + drag-drop), table list, download,
 * and soft-delete. Every read goes through /api/v1/documents/<id>/download/
 * which checks permissions and writes an audit row server-side; we never
 * embed a direct media URL.
 */

import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  Upload, Download, MoreVertical, FileText, File as FileIcon,
  Image as ImageIcon, FileSpreadsheet, Presentation,
} from 'lucide-react'
import { api } from '../lib/api'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useDeleteConfirm, extractErr } from '@/components/ui/inline-edit'

// ───────── Types ─────────

type DocumentRow = {
  id: string
  content_type: number
  object_id: number
  parent_label: string
  original_filename: string
  display_name: string
  title: string
  description: string
  mime: string
  bytes: number
  sha256: string
  kind: 'UPLOAD' | 'GENERATED'
  uploaded_by: number | null
  uploaded_by_username: string | null
  created_at: string
  deleted_at: string | null
  download_url: string
}

// File-input `accept` whitelist — must stay in sync with backend
// core/storage.py ALLOWED_MIMES. Listed by extension since browsers vary on
// the MIME they send for old-format Office files.
const ACCEPT =
  '.pdf,.docx,.xlsx,.pptx,.doc,.xls,.csv,.txt,.png,.jpg,.jpeg,' +
  'application/pdf,' +
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document,' +
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,' +
  'application/vnd.openxmlformats-officedocument.presentationml.presentation,' +
  'application/msword,application/vnd.ms-excel,' +
  'text/csv,text/plain,image/png,image/jpeg'

const MAX_BYTES = 25 * 1024 * 1024

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

function iconFor(mime: string) {
  if (mime.startsWith('image/')) return ImageIcon
  if (mime === 'application/pdf') return FileText
  if (mime.includes('spreadsheet') || mime === 'application/vnd.ms-excel') return FileSpreadsheet
  if (mime.includes('presentation')) return Presentation
  return FileIcon
}

function shortMime(mime: string): string {
  // Friendlier label than the full vendor-prefixed MIME.
  const map: Record<string, string> = {
    'application/pdf': 'PDF',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PowerPoint',
    'application/msword': 'Word (legacy)',
    'application/vnd.ms-excel': 'Excel (legacy)',
    'text/csv': 'CSV',
    'text/plain': 'Text',
    'image/png': 'PNG',
    'image/jpeg': 'JPEG',
  }
  return map[mime] || mime || '—'
}

// ───────── Component ─────────

export default function FilesTab({
  parent, parentId,
}: { parent: string; parentId: number }) {
  const qc = useQueryClient()
  const { confirm, dialog } = useDeleteConfirm()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)

  const parentKey = `${parent}:${parentId}`
  const queryKey = ['core', 'documents', parentKey]

  const listQ = useQuery({
    queryKey,
    queryFn: async () =>
      (await api.get<DocumentRow[]>(`/documents/?parent=${encodeURIComponent(parentKey)}`)).data,
    enabled: !!parentId,
  })

  const uploadMut = useMutation({
    mutationFn: async (file: File) => {
      if (file.size > MAX_BYTES) {
        throw new Error(`"${file.name}" is larger than 25 MB.`)
      }
      const fd = new FormData()
      fd.append('parent', parentKey)
      fd.append('file', file)
      // Let axios set the multipart boundary itself — don't override Content-Type.
      return (await api.post<DocumentRow>('/documents/', fd)).data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey })
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const deleteMut = useMutation({
    mutationFn: async (id: string) => api.delete(`/documents/${id}/`),
    onSuccess: () => {
      toast.success('File deleted')
      qc.invalidateQueries({ queryKey })
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  async function downloadFile(doc: DocumentRow) {
    try {
      // Fetch the bytes with auth, then trigger a browser download. We
      // cannot use a plain <a href> because the JWT only lives in localStorage
      // and the browser would not attach it to a top-level navigation.
      const res = await api.get(doc.download_url.replace(/^\/api\/v1/, ''), {
        responseType: 'blob',
      })
      const url = URL.createObjectURL(res.data as Blob)
      const a = document.createElement('a')
      a.href = url
      a.download = doc.original_filename || 'file'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      // Revoke after a tick so Safari has time to start the save.
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    } catch (e) {
      toast.error(extractErr(e))
    }
  }

  function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return
    const arr = Array.from(files)
    let successes = 0
    let toastedDone = false
    const total = arr.length

    arr.forEach((f) => {
      uploadMut.mutate(f, {
        onSuccess: () => {
          successes += 1
          if (!toastedDone && successes === total) {
            toastedDone = true
            toast.success(total === 1 ? 'File uploaded' : `${total} files uploaded`)
          }
        },
      })
    })
  }

  function onDrop(ev: React.DragEvent<HTMLDivElement>) {
    ev.preventDefault()
    setDragOver(false)
    handleFiles(ev.dataTransfer.files)
  }

  const docs = listQ.data || []

  return (
    <div>
      {dialog}
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT}
        multiple
        className="hidden"
        onChange={(e) => {
          handleFiles(e.target.files)
          // Reset so picking the same file twice in a row still fires onChange.
          if (e.target) e.target.value = ''
        }}
      />

      <div className="flex items-center justify-between mb-4 gap-4">
        <p className="text-sm text-muted-foreground">
          Files attached to this record. PDF, Word, Excel, PowerPoint, CSV, TXT, PNG, JPG. Max 25 MB each.
          All downloads are permission-checked and audit-logged.
        </p>
        <Button onClick={() => fileInputRef.current?.click()} disabled={uploadMut.isPending}>
          <Upload className="h-3 w-3 mr-1" />
          {uploadMut.isPending ? 'Uploading…' : 'Upload file'}
        </Button>
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`rounded-lg border-2 border-dashed transition-colors ${
          dragOver ? 'border-violet-400 bg-violet-50' : 'border-border'
        }`}
      >
        {listQ.isLoading ? (
          <p className="p-8 text-center text-muted-foreground text-sm">Loading…</p>
        ) : docs.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground text-sm">
            <Upload className="h-6 w-6 mx-auto mb-2 opacity-40" />
            No files yet. Click <span className="font-medium">Upload file</span> or drag files into this area.
          </div>
        ) : (
          <div className="bg-card rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-muted/40">
                <tr>
                  <Th>Name</Th>
                  <Th>Type</Th>
                  <Th className="text-right">Size</Th>
                  <Th>Uploaded by</Th>
                  <Th>Date</Th>
                  <Th />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {docs.map((d) => {
                  const Icon = iconFor(d.mime)
                  return (
                    <tr key={d.id} className="hover:bg-muted/30">
                      <Td>
                        <button
                          type="button"
                          onClick={() => downloadFile(d)}
                          className="inline-flex items-center gap-2 text-violet-600 hover:underline text-sm text-left"
                          title="Download"
                        >
                          <Icon className="h-4 w-4 shrink-0" />
                          <span className="truncate max-w-[24rem]">{d.display_name}</span>
                        </button>
                      </Td>
                      <Td className="text-xs text-muted-foreground">{shortMime(d.mime)}</Td>
                      <Td className="text-xs text-muted-foreground text-right whitespace-nowrap">
                        {formatBytes(d.bytes)}
                      </Td>
                      <Td className="text-xs">{d.uploaded_by_username || '—'}</Td>
                      <Td className="text-xs text-muted-foreground whitespace-nowrap">
                        {new Date(d.created_at).toLocaleDateString()}
                      </Td>
                      <Td className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                              <MoreVertical className="h-4 w-4" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => downloadFile(d)}>
                              <Download className="h-3.5 w-3.5 mr-2" /> Download
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => confirm(d.display_name, () => deleteMut.mutate(d.id))}
                            >
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </Td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

// ───────── Local helpers ─────────

function Th({ children, className = '' }: { children?: React.ReactNode; className?: string }) {
  return (
    <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide ${className}`}>
      {children}
    </th>
  )
}

function Td({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-4 py-3 ${className}`}>{children}</td>
}
